/* eslint-disable react-compiler/react-compiler */
import Form from '@rjsf/core'
import {
  RegistryFieldsType,
  RegistryWidgetsType,
  RJSFValidationError,
  getDefaultFormState,
} from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { CorrosionClassificationField } from 'features/Corrosion/CorrosionClassificationField'
import { isEqual } from 'lodash'
import { AdditionalWorkingStepFormConfiguration } from 'model/AdditionalWorkingStep/AdditionalWorkingStepForm'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useShowException } from 'utils/useShowException'
import { WorkingStepFormValidator } from './WorkingStepFormValidator'
import { ArrayFieldTemplate } from './rjsf/Templates/ArrayFieldTemplate'
import { FieldTemplate } from './rjsf/Templates/FieldTemplate'
import { ObjectFieldTemplate } from './rjsf/Templates/ObjectFieldTemplate'
import { RJSFQuantityField } from './rjsf/Templates/RJSFQuantityField'
import { RJSFBaseInputTemplate } from './rjsf/Templates/RJSFTextField'
import { RalSelectorField } from './rjsf/Templates/RalSelectorField'
import { CheckboxField } from './rjsf/Widgets/CheckboxField'
import { ChemicalBlackeningField } from './rjsf/Templates/ChemicalBlackeningField'
import { SelectWidget } from './rjsf/Widgets/SelectWidget'
import { ZincCodeWidget } from './rjsf/Widgets/ZincCodeWidget'
import { WorkingStepWithData } from './types/WorkingStepWithData'

export type NewWorkingStepFormRef = {
  refreshData: () => Promise<void>
  getValidations: () => boolean
}

const customFields: RegistryFieldsType = {
  quantityField: RJSFQuantityField as never,
  ralSelectorField: RalSelectorField as never,
  corrosionClassificationField: CorrosionClassificationField as never,
  zincCode: ZincCodeWidget as never,
  BooleanField: CheckboxField,
  chemicalBlackeningField: ChemicalBlackeningField as never,
}

const customWidgets: RegistryWidgetsType = {
  SelectWidget: SelectWidget,
}

const templates = {
  FieldTemplate: FieldTemplate,
  ArrayFieldTemplate: ArrayFieldTemplate,
  ObjectFieldTemplate: ObjectFieldTemplate,
  ButtonTemplates: {
    SubmitButton: () => null,
  },
  FieldErrorTemplate: () => null,
  BaseInputTemplate: RJSFBaseInputTemplate,
}

type Props = {
  ref: React.RefObject<NewWorkingStepFormRef>
  bomItemPointer: BomItemPointer
  workingStep: WorkingStepWithData
  onChange: <T extends keyof WorkingStepWithData>(
    field: T,
    value: WorkingStepWithData[T],
  ) => void
  onError: (errros: RJSFValidationError[]) => void
  projectId: string
  controller: WorkingStepsController
  loading: Record<string, boolean>
  onValidationError?: (
    workingStepWithData: WorkingStepWithData,
    errors: Record<string, { __errors: string[] }>,
  ) => void
  removeValidations?: (
    primaryWorkingStep: WorkingStepType,
    itemId: string,
  ) => void
}

const applyDraft = (
  serverForm: AdditionalWorkingStepFormConfiguration | null,
  draft: Record<string, unknown> | null | undefined,
) =>
  !serverForm || draft == null ? serverForm : { ...serverForm, formData: draft }

export const WorkingStepForm = (props: Props) => {
  const [form, setForm] = useState<AdditionalWorkingStepFormConfiguration>(null)
  const [validationErrors, setValidationErrors] = useState<
    RJSFValidationError[]
  >([])
  const { projectId, controller, onError } = props
  const ShowException = useShowException('working step form')
  const requestId = useRef(0)
  const mounted = useRef(true)

  const activityId = props.workingStep?.bomItemActivityId
  const secondaryWorkingStep = props.workingStep?.secondaryWorkingStep
  const bomItemId = props.bomItemPointer?.id
  const bomItemType = props.bomItemPointer?.type

  const getWorkingStepDetails = useCallback(async () => {
    try {
      return await controller.GetWorkingStepForm(
        projectId,
        { id: bomItemId, type: bomItemType },
        activityId,
        secondaryWorkingStep,
      )
    } catch (err) {
      ShowException(err)
      return null
    }
  }, [
    ShowException,
    controller,
    projectId,
    bomItemId,
    bomItemType,
    activityId,
    secondaryWorkingStep,
  ])

  const getValidationErrors = useCallback(
    (configuration: AdditionalWorkingStepFormConfiguration | null) => {
      if (!configuration || configuration.jsonSchema?.title === 'EmptyDto') {
        return []
      }

      return validator.validateFormData(
        configuration.formData,
        configuration.jsonSchema,
        WorkingStepFormValidator,
        undefined,
        configuration.uiSchema,
      ).errors
    },
    [],
  )

  useImperativeHandle(
    props.ref,
    () => ({
      refreshData: async () => {
        const currentRequest = ++requestId.current
        const updatedForm = await getWorkingStepDetails()
        if (mounted.current && currentRequest === requestId.current) {
          setForm(updatedForm)
          setValidationErrors(getValidationErrors(updatedForm))
        }
      },
      getValidations: () => {
        return Boolean(form) && validationErrors.length === 0
      },
    }),
    [form, getValidationErrors, getWorkingStepDetails, validationErrors.length],
  )

  useEffect(() => {
    const currentRequest = ++requestId.current
    setForm(null)
    setValidationErrors([])
    void getWorkingStepDetails().then((serverData) => {
      if (mounted.current && currentRequest === requestId.current) {
        const loadedForm =
          serverData?.jsonSchema?.title === 'EmptyDto'
            ? serverData
            : applyDraft(serverData, props.workingStep?.formData)
        setForm(loadedForm)
        setValidationErrors(getValidationErrors(loadedForm))
      }
    })
    // Draft edits are handled locally; only a different target reloads the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getValidationErrors,
    getWorkingStepDetails,
    props.workingStep?.bomItemActivityId,
    props.workingStep?.secondaryWorkingStep,
  ])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      requestId.current += 1
    }
  }, [])

  useEffect(() => {
    onError(validationErrors)
  }, [onError, validationErrors])

  if (!props.workingStep) {
    return null
  }

  if (!form) {
    return null
  }

  if (form?.jsonSchema?.title === 'EmptyDto') {
    return null
  }

  const { onChange } = props

  return (
    <div className={'ws__form__wrapper'}>
      <Form
        liveValidate={true}
        showErrorList={false}
        templates={templates}
        fields={customFields}
        widgets={customWidgets}
        schema={form.jsonSchema}
        uiSchema={form.uiSchema}
        formData={form.formData}
        onChange={(data) => {
          const normalizedCurrentData = getDefaultFormState(
            validator,
            form.jsonSchema,
            form.formData,
            form.jsonSchema,
          )
          const isNormalizationOnly = isEqual(
            data.formData,
            normalizedCurrentData,
          )
          const nextForm = { ...form, formData: data.formData }
          const authoritativeErrors = getValidationErrors(nextForm)

          setForm(nextForm)
          setValidationErrors(
            data.errors?.length ? data.errors : authoritativeErrors,
          )

          if (!isNormalizationOnly) {
            requestId.current += 1
            onChange('formData', data.formData)
          }
        }}
        validator={validator}
        customValidate={WorkingStepFormValidator as any}
      />
      {/* <JsonViewer data={form} /> */}
    </div>
  )
}
